import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { VERSION_DDJJ_MEDICO, TEXTO_DDJJ_MEDICO } from '@ausentia/shared';
import { api, ErrorApi } from '../api/cliente.js';
import { useToast } from '../componentes/Toast.js';

interface Medico {
  id: string;
  nombre: string;
  email: string;
  matriculaTipo: string;
  matriculaNumero: string;
  matriculaJurisdiccion: string;
  refepsValidada: number;
  firmaEnrolado: number;
  ddjjVersion: string | null;
  activo: number;
  creadoEn: string;
}

interface AdjuntoFoja {
  id: string;
  tipo: string;
  hashSha256: string;
  mime: string;
  bytes: number;
  recibidoEn: string;
}

interface EventoFoja {
  seq: number;
  ts: string;
  tipo: string;
  actor: string;
  datos: Record<string, unknown>;
}

interface FojaData {
  medico: Medico;
  adjuntos: AdjuntoFoja[];
  eventos: EventoFoja[];
}

async function procesarArchivo(file: File): Promise<{ contenidoBase64: string; hashSha256: string; mime: string }> {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  const hashSha256 = Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  const uint8 = new Uint8Array(buffer);
  const chunks: string[] = [];
  const CHUNK = 32768;
  for (let i = 0; i < uint8.length; i += CHUNK) {
    chunks.push(String.fromCharCode(...Array.from(uint8.subarray(i, i + CHUNK))));
  }
  const contenidoBase64 = btoa(chunks.join(''));
  return { contenidoBase64, hashSha256, mime: file.type };
}

export function Medicos() {
  const [medicos, setMedicos] = useState<Medico[] | null>(null);
  const [error, setError] = useState('');
  const toast = useToast();

  // Modal registrar
  const [modalAbierto, setModalAbierto] = useState(false);
  const [nombre, setNombre] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [matriculaTipo, setMatriculaTipo] = useState('MP');
  const [matriculaNumero, setMatriculaNumero] = useState('');
  const [matriculaJurisdiccion, setMatriculaJurisdiccion] = useState('AR-D');
  const [creando, setCreando] = useState(false);
  const [errorForm, setErrorForm] = useState('');

  // Foja panel
  const [fojaMediacoId, setFojaMedicoId] = useState<string | null>(null);
  const [foja, setFoja] = useState<FojaData | null>(null);
  const [cargandoFoja, setCargandoFoja] = useState(false);
  const [subiendoTipo, setSubiendoTipo] = useState<string | null>(null);
  const [habilitando, setHabilitando] = useState(false);
  const [suspendiendo, setSuspendiendo] = useState(false);
  const [ddjjExpandida, setDdjjExpandida] = useState(false);

  async function cargarMedicos() {
    try {
      setMedicos(await api.get<Medico[]>('/api/operador/medicos'));
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudieron cargar los médicos');
    }
  }

  async function cargarFoja(medicoId: string) {
    setCargandoFoja(true);
    try {
      setFoja(await api.get<FojaData>(`/api/operador/medicos/${medicoId}/foja`));
    } catch (err) {
      alert(err instanceof ErrorApi ? err.message : 'Error al cargar la foja');
    } finally {
      setCargandoFoja(false);
    }
  }

  async function abrirFoja(medicoId: string) {
    setFojaMedicoId(medicoId);
    setFoja(null);
    setDdjjExpandida(false);
    await cargarFoja(medicoId);
  }

  function cerrarFoja() {
    setFojaMedicoId(null);
    setFoja(null);
  }

  async function subirDocumento(file: File, tipo: 'ENROLAMIENTO_FORMULARIO' | 'ENROLAMIENTO_DOCUMENTO') {
    setSubiendoTipo(tipo);
    try {
      const { contenidoBase64, hashSha256, mime } = await procesarArchivo(file);
      await api.post(`/api/operador/medicos/${fojaMediacoId}/adjunto-enrolamiento`, {
        tipo,
        contenidoBase64,
        hashSha256,
        mime,
        capturadaEn: new Date().toISOString(),
        geo: null,
      });
      toast.mostrar('Documento subido correctamente.');
      await cargarFoja(fojaMediacoId!);
    } catch (err) {
      alert(err instanceof ErrorApi ? err.message : 'Error al subir el documento');
    } finally {
      setSubiendoTipo(null);
    }
  }

  async function habilitar() {
    setHabilitando(true);
    try {
      await api.post(`/api/operador/medicos/${fojaMediacoId}/habilitar`, {
        ddjjVersion: VERSION_DDJJ_MEDICO,
      });
      toast.mostrar('Médico habilitado correctamente.');
      await Promise.all([cargarFoja(fojaMediacoId!), cargarMedicos()]);
    } catch (err) {
      alert(err instanceof ErrorApi ? err.message : 'Error al habilitar el médico');
    } finally {
      setHabilitando(false);
    }
  }

  async function suspender() {
    if (!confirm('¿Confirmar suspensión de habilitación? Las firmas ya emitidas quedan intactas.')) return;
    setSuspendiendo(true);
    try {
      await api.post(`/api/operador/medicos/${fojaMediacoId}/suspender`, {});
      toast.mostrar('Habilitación suspendida.');
      await Promise.all([cargarFoja(fojaMediacoId!), cargarMedicos()]);
    } catch (err) {
      alert(err instanceof ErrorApi ? err.message : 'Error al suspender la habilitación');
    } finally {
      setSuspendiendo(false);
    }
  }

  async function toggleActivoMedico(medicoId: string) {
    try {
      await api.post(`/api/operador/medicos/${medicoId}/toggle-activo`, {});
      toast.mostrar('Estado del médico actualizado.');
      await cargarMedicos();
    } catch (err) {
      alert(err instanceof ErrorApi ? err.message : 'Error al cambiar estado del médico');
    }
  }

  useEffect(() => {
    void cargarMedicos();
  }, []);

  async function registrar(e: React.FormEvent) {
    e.preventDefault();
    if (!nombre || !email || !password || !matriculaNumero || !matriculaJurisdiccion) {
      setErrorForm('Por favor completa todos los campos obligatorios (*).');
      return;
    }
    setCreando(true);
    setErrorForm('');
    try {
      await api.post('/api/operador/medicos', {
        nombre, email, password, matriculaTipo, matriculaNumero, matriculaJurisdiccion,
      });
      toast.mostrar(`Médico Dr/a. ${nombre} registrado correctamente.`);
      cerrarModalLimpiando();
      await cargarMedicos();
    } catch (err) {
      setErrorForm(err instanceof ErrorApi ? err.message : 'Error al registrar el médico');
    } finally {
      setCreando(false);
    }
  }

  function cerrarModalLimpiando() {
    setModalAbierto(false);
    setNombre(''); setEmail(''); setPassword('');
    setMatriculaTipo('MP'); setMatriculaNumero(''); setMatriculaJurisdiccion('AR-D');
    setErrorForm('');
  }

  const habilitado = (m: Medico) => m.refepsValidada === 1 && m.firmaEnrolado === 1;

  return (
    <section style={{ animation: 'fadeInUp 0.4s ease forwards' }}>
      <div className="fila" style={{ marginBottom: 20, alignItems: 'center' }}>
        <div>
          <h2>🩺 Médicos Auditores</h2>
          <p className="muted">
            Gestioná los profesionales habilitados para realizar auditorías médicas.
          </p>
        </div>
        <button className="primario" style={{ width: 'auto' }} onClick={() => setModalAbierto(true)}>
          + Registrar Médico
        </button>
      </div>

      {error && <div className="error-box">{error}</div>}
      {medicos === null ? (
        <div className="cargando">Cargando nómina de médicos…</div>
      ) : medicos.length === 0 ? (
        <p className="muted">No hay médicos registrados.</p>
      ) : (
        <div className="tabla-contenedor">
          <table className="tabla">
            <thead>
              <tr>
                <th>Médico</th>
                <th>Matrícula</th>
                <th>Habilitación</th>
                <th>Estado</th>
                <th>Fecha Alta</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {medicos.map((m) => (
                <tr key={m.id}>
                  <td>
                    <strong>{m.nombre}</strong>
                    <div className="mini muted">{m.email} · {m.id}</div>
                  </td>
                  <td>
                    <span style={{ fontWeight: 600 }}>{m.matriculaTipo} {m.matriculaNumero}</span>
                    <div className="mini muted">{m.matriculaJurisdiccion}</div>
                  </td>
                  <td>
                    <span className={`insignia ${habilitado(m) ? 'verde' : 'ambar'}`}>
                      {habilitado(m) ? 'HABILITADO' : 'PENDIENTE'}
                    </span>
                    {m.ddjjVersion && (
                      <div className="mini muted">{m.ddjjVersion}</div>
                    )}
                  </td>
                  <td>
                    <span className={`insignia ${m.activo === 1 ? 'verde' : 'roja'}`}>
                      {m.activo === 1 ? 'ACTIVO' : 'INACTIVO'}
                    </span>
                  </td>
                  <td>{m.creadoEn.slice(0, 10)}</td>
                  <td style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="secundario mini"
                      onClick={() => void abrirFoja(m.id)}
                      style={{ width: 'auto' }}
                    >
                      Foja
                    </button>
                    <button
                      type="button"
                      className="secundario mini"
                      onClick={() => void toggleActivoMedico(m.id)}
                      style={{ width: 'auto' }}
                    >
                      {m.activo === 1 ? 'Desactivar' : 'Activar'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal registrar médico */}
      {modalAbierto && createPortal(
        <div className="capa-bloqueo" onClick={(e) => { if (e.target === e.currentTarget) cerrarModalLimpiando(); }}>
          <div className="dialogo glass">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--borde)', paddingBottom: 12, marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 18 }}>Registrar Médico Auditor</h3>
              <button type="button" className="fantasma" onClick={cerrarModalLimpiando} style={{ fontSize: 20, padding: 4, lineHeight: 1 }}>✕</button>
            </div>

            {errorForm && <div className="error-box">{errorForm}</div>}

            <form onSubmit={(e) => void registrar(e)}>
              <div className="campo" style={{ marginBottom: 12 }}>
                <label htmlFor="medicoNombre">Nombre Completo (*)</label>
                <input id="medicoNombre" type="text" required value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Ej. Dr. Diego García" />
              </div>
              <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                <div className="campo" style={{ flex: 1, marginBottom: 0 }}>
                  <label htmlFor="medicoEmail">Correo Electrónico (*)</label>
                  <input id="medicoEmail" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="medico@correo.com" />
                </div>
                <div className="campo" style={{ flex: 1, marginBottom: 0 }}>
                  <label htmlFor="medicoPassword">Contraseña (*)</label>
                  <input id="medicoPassword" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Mínimo 6 caracteres" />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
                <div className="campo" style={{ width: 120, marginBottom: 0 }}>
                  <label htmlFor="medicoMatTipo">Tipo Mat. (*)</label>
                  <select id="medicoMatTipo" value={matriculaTipo} onChange={(e) => setMatriculaTipo(e.target.value)}>
                    <option value="MP">MP (Provincial)</option>
                    <option value="MN">MN (Nacional)</option>
                  </select>
                </div>
                <div className="campo" style={{ flex: 1, marginBottom: 0 }}>
                  <label htmlFor="medicoMatNum">Nro. Matrícula (*)</label>
                  <input id="medicoMatNum" type="text" required value={matriculaNumero} onChange={(e) => setMatriculaNumero(e.target.value)} placeholder="Ej. 12345" />
                </div>
                <div className="campo" style={{ flex: 1, marginBottom: 0 }}>
                  <label htmlFor="medicoMatJuris">Jurisdicción (*)</label>
                  <input id="medicoMatJuris" type="text" required value={matriculaJurisdiccion} onChange={(e) => setMatriculaJurisdiccion(e.target.value)} placeholder="Ej. AR-D (San Luis)" />
                </div>
              </div>
              <div className="fila" style={{ gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
                <button type="button" className="secundario" onClick={cerrarModalLimpiando} disabled={creando}>Cancelar</button>
                <button type="submit" className="primario" disabled={creando}>{creando ? 'Registrando…' : 'Registrar Médico'}</button>
              </div>
            </form>
          </div>
        </div>,
        document.body,
      )}

      {/* Panel foja del médico */}
      {fojaMediacoId && createPortal(
        <div className="capa-bloqueo" onClick={(e) => { if (e.target === e.currentTarget) cerrarFoja(); }}>
          <div className="dialogo glass" style={{ maxWidth: 680, width: '92vw', maxHeight: '88vh', overflowY: 'auto' }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '1px solid var(--borde)', paddingBottom: 12, marginBottom: 20 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 18 }}>Foja del médico</h3>
                {foja && <div className="mini muted">{foja.medico.nombre} · {foja.medico.email}</div>}
              </div>
              <button type="button" className="fantasma" onClick={cerrarFoja} style={{ fontSize: 20, padding: 4, lineHeight: 1 }}>✕</button>
            </div>

            {cargandoFoja ? (
              <div className="cargando">Cargando foja…</div>
            ) : foja ? (
              <>
                {/* Estado de habilitación */}
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, color: 'var(--texto-muted)' }}>ESTADO DE HABILITACIÓN</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span className={`insignia ${foja.medico.refepsValidada === 1 && foja.medico.firmaEnrolado === 1 ? 'verde' : 'ambar'}`} style={{ fontSize: 14 }}>
                      {foja.medico.refepsValidada === 1 && foja.medico.firmaEnrolado === 1 ? 'HABILITADO' : 'PENDIENTE'}
                    </span>
                    {foja.medico.ddjjVersion && (
                      <span className="mini muted">DDJJ: {foja.medico.ddjjVersion}</span>
                    )}
                  </div>
                </div>

                {/* Documentos de enrolamiento */}
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12, color: 'var(--texto-muted)' }}>DOCUMENTOS DE ENROLAMIENTO</div>

                  {/* DDJJ Formulario firmado */}
                  <div style={{ marginBottom: 16, padding: 12, border: '1px solid var(--borde)', borderRadius: 8 }}>
                    <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 14 }}>Formulario DDJJ firmado</div>
                    {foja.adjuntos.filter((a) => a.tipo === 'ENROLAMIENTO_FORMULARIO').length === 0 ? (
                      <div className="mini muted" style={{ marginBottom: 8 }}>Sin formularios subidos aún</div>
                    ) : (
                      foja.adjuntos
                        .filter((a) => a.tipo === 'ENROLAMIENTO_FORMULARIO')
                        .map((a) => (
                          <div key={a.id} className="mini" style={{ marginBottom: 4, color: 'var(--verde)' }}>
                            ✓ {a.id.slice(0, 20)}… · {(a.bytes / 1024).toFixed(0)} KB · {a.recibidoEn.slice(0, 10)}
                          </div>
                        ))
                    )}
                    <label style={{ display: 'inline-block', cursor: subiendoTipo ? 'not-allowed' : 'pointer' }}>
                      <span className="secundario mini" style={{ width: 'auto', display: 'inline-block', padding: '4px 10px', fontSize: 12 }}>
                        {subiendoTipo === 'ENROLAMIENTO_FORMULARIO' ? 'Subiendo…' : '+ Subir formulario'}
                      </span>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        disabled={subiendoTipo !== null}
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) void subirDocumento(f, 'ENROLAMIENTO_FORMULARIO');
                          e.target.value = '';
                        }}
                      />
                    </label>
                  </div>

                  {/* Foto del DNI */}
                  <div style={{ marginBottom: 8, padding: 12, border: '1px solid var(--borde)', borderRadius: 8 }}>
                    <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 14 }}>Foto del DNI</div>
                    {foja.adjuntos.filter((a) => a.tipo === 'ENROLAMIENTO_DOCUMENTO').length === 0 ? (
                      <div className="mini muted" style={{ marginBottom: 8 }}>Sin fotos de DNI subidas aún</div>
                    ) : (
                      foja.adjuntos
                        .filter((a) => a.tipo === 'ENROLAMIENTO_DOCUMENTO')
                        .map((a) => (
                          <div key={a.id} className="mini" style={{ marginBottom: 4, color: 'var(--verde)' }}>
                            ✓ {a.id.slice(0, 20)}… · {(a.bytes / 1024).toFixed(0)} KB · {a.recibidoEn.slice(0, 10)}
                          </div>
                        ))
                    )}
                    <label style={{ display: 'inline-block', cursor: subiendoTipo ? 'not-allowed' : 'pointer' }}>
                      <span className="secundario mini" style={{ width: 'auto', display: 'inline-block', padding: '4px 10px', fontSize: 12 }}>
                        {subiendoTipo === 'ENROLAMIENTO_DOCUMENTO' ? 'Subiendo…' : '+ Subir foto DNI'}
                      </span>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        disabled={subiendoTipo !== null}
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) void subirDocumento(f, 'ENROLAMIENTO_DOCUMENTO');
                          e.target.value = '';
                        }}
                      />
                    </label>
                  </div>
                </div>

                {/* DDJJ texto expandible */}
                <div style={{ marginBottom: 20 }}>
                  <button
                    type="button"
                    className="fantasma"
                    onClick={() => setDdjjExpandida(!ddjjExpandida)}
                    style={{ fontSize: 13, textDecoration: 'underline', padding: 0 }}
                  >
                    {ddjjExpandida ? '▾' : '▸'} Ver texto de la Declaración Jurada ({VERSION_DDJJ_MEDICO})
                  </button>
                  {ddjjExpandida && (
                    <div style={{ marginTop: 10, padding: 14, background: 'var(--fondo-alternado, rgba(0,0,0,0.04))', borderRadius: 8, fontSize: 13 }}>
                      <strong style={{ display: 'block', marginBottom: 8 }}>{TEXTO_DDJJ_MEDICO.titulo}</strong>
                      <ol style={{ margin: '0 0 8px', paddingLeft: 20 }}>
                        {TEXTO_DDJJ_MEDICO.clausulas.map((c, i) => (
                          <li key={i} style={{ marginBottom: 6 }}>{c}</li>
                        ))}
                      </ol>
                      <em style={{ fontSize: 12, color: 'var(--texto-muted)' }}>{TEXTO_DDJJ_MEDICO.pieDeClausula}</em>
                    </div>
                  )}
                </div>

                {/* Acciones de habilitación */}
                <div style={{ borderTop: '1px solid var(--borde)', paddingTop: 16, marginBottom: 20 }}>
                  {foja.medico.refepsValidada !== 1 || foja.medico.firmaEnrolado !== 1 ? (
                    <div>
                      {(() => {
                        const tieneFormulario = foja.adjuntos.some((a) => a.tipo === 'ENROLAMIENTO_FORMULARIO');
                        const tieneDoc = foja.adjuntos.some((a) => a.tipo === 'ENROLAMIENTO_DOCUMENTO');
                        const puedeHabilitar = tieneFormulario && tieneDoc;
                        return (
                          <>
                            {!puedeHabilitar && (
                              <div className="error-box" style={{ marginBottom: 12, fontSize: 13 }}>
                                Falta subir:{' '}
                                {[
                                  !tieneFormulario && 'formulario DDJJ firmado',
                                  !tieneDoc && 'foto del DNI',
                                ].filter(Boolean).join(' y ')}
                              </div>
                            )}
                            <button
                              type="button"
                              className="primario"
                              disabled={!puedeHabilitar || habilitando}
                              onClick={() => void habilitar()}
                              style={{ width: 'auto' }}
                            >
                              {habilitando ? 'Habilitando…' : 'Habilitar médico'}
                            </button>
                          </>
                        );
                      })()}
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="secundario"
                      disabled={suspendiendo}
                      onClick={() => void suspender()}
                      style={{ width: 'auto' }}
                    >
                      {suspendiendo ? 'Suspendiendo…' : 'Suspender habilitación'}
                    </button>
                  )}
                </div>

                {/* Historial de eventos */}
                {foja.eventos.length > 0 && (
                  <div style={{ borderTop: '1px solid var(--borde)', paddingTop: 16 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10, color: 'var(--texto-muted)' }}>HISTORIAL</div>
                    <div style={{ fontSize: 12 }}>
                      {foja.eventos.map((e) => (
                        <div key={e.seq} style={{ marginBottom: 6, display: 'flex', gap: 8 }}>
                          <span className="muted" style={{ flexShrink: 0 }}>{e.ts.slice(0, 16).replace('T', ' ')}</span>
                          <strong>{e.tipo}</strong>
                          <span className="muted">{e.actor}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : null}
          </div>
        </div>,
        document.body,
      )}
    </section>
  );
}
